<template>
  <div class="chart-container">
    <canvas ref="chartCanvas"></canvas>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { Chart } from 'chart.js/auto'

export default {
  name: 'LineChart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    buildNumbers: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: '#128078'
    },
    unit: {
      type: String,
      default: '%'
    }
  },
  emits: ['legend-click'],
  setup(props, { emit }) {
    const chartCanvas = ref(null)
    let chart = null

    const createChart = () => {
      if (!chartCanvas.value) return

      const ctx = chartCanvas.value.getContext('2d')
      
      const labels = props.data.map(item => item.date)
      const values = props.data.map(item => item.value)

      const colors = [
        '#ff4757', '#3742fa', '#2ed573', '#ffa502', '#ff6348',
        '#1e90ff', '#ff00ff', '#00ced1', '#ffd700', '#ff1493', '#00ff7f'
      ]

      const datasets = [
        {
          label: 'Total',
          data: values,
          borderColor: colors[0],
          backgroundColor: colors[0] + '20',
          borderWidth: 3,
          fill: false,
          tension: 0.2,
          pointBackgroundColor: colors[0],
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7
        }
      ]

      if (props.buildNumbers && props.buildNumbers.length > 0) {
        props.buildNumbers.forEach((buildNum, index) => {
          const buildData = props.data.map(item => {
            const build = item.builds?.[buildNum]
            return build && build.rate !== null ? build.rate : 0
          })

          datasets.push({
            label: buildNum,
            data: buildData,
            borderColor: colors[index + 1] || colors[index % colors.length],
            backgroundColor: 'transparent',
            borderWidth: 2,
            fill: false,
            tension: 0.2,
            pointBackgroundColor: colors[index + 1] || colors[index % colors.length],
            pointBorderColor: '#fff',
            pointBorderWidth: 1,
            pointRadius: 3,
            pointHoverRadius: 5,
            spanGaps: false
          })
        })
      }

      chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              labels: {
                color: '#fff',
                padding: 15,
                font: {
                  size: 12
                },
                usePointStyle: true
              },
              onClick: (e, legendItem, legend) => {
                const index = legendItem.datasetIndex
                const ci = legend.chart
                
                if (ci.isDatasetVisible(index)) {
                  ci.hide(index)
                } else {
                  ci.show(index)
                }
                
                // Get visible datasets
                const visibleDatasets = []
                ci.data.datasets.forEach((dataset, i) => {
                  if (ci.isDatasetVisible(i)) {
                    visibleDatasets.push(dataset.label)
                  }
                })
                
                // Emit to parent
                emit('legend-click', visibleDatasets)
              }
            },
            tooltip: {
              backgroundColor: '#1a1b2e',
              titleColor: '#fff',
              bodyColor: '#a9a9b3',
              borderColor: '#2c2d42',
              borderWidth: 1,
              padding: 12,
              displayColors: true,
              callbacks: {
                title: (context) => {
                  return context[0].label
                },
                label: (context) => {
                  const index = context.dataIndex
                  const dataPoint = props.data[index]
                  const datasetLabel = context.dataset.label
                  const u = props.unit

                  if (datasetLabel === 'Total') {
                    if (dataPoint && dataPoint.started !== undefined && dataPoint.cancelled !== undefined) {
                      return [
                        `Total: ${dataPoint.value}${u}`,
                        `Started: ${dataPoint.started}`,
                        `Cancelled: ${dataPoint.cancelled}`
                      ]
                    }
                    if (dataPoint && dataPoint.count !== undefined) {
                      return [
                        `Avg: ${dataPoint.value}${u}`,
                        `Samples: ${dataPoint.count}`
                      ]
                    }
                  } else {
                    const build = dataPoint.builds?.[datasetLabel]
                    if (build && build.rate !== null) {
                      return [
                        `${datasetLabel}: ${build.rate}${u}`,
                        `Started: ${build.started}`,
                        `Cancelled: ${build.cancelled}`
                      ]
                    }
                    return `${datasetLabel}: No data`
                  }

                  return `${datasetLabel}: ${context.parsed.y}${u}`
                }
              }
            }
          },
          scales: {
            x: {
              display: true,
              grid: {
                display: false
              },
              ticks: {
                color: '#8b8b8b',
                font: {
                  size: 12
                }
              }
            },
            y: {
              display: true,
              beginAtZero: true,
              min: 0,
              grid: {
                color: '#2a2a2a'
              },
              ticks: {
                color: '#8b8b8b',
                font: {
                  size: 12
                },
                callback: (value) => `${value}${props.unit}`
              }
            }
          }
        }
      })
    }

    const updateChart = () => {
      if (chart) {
        chart.destroy()
      }
      createChart()
    }

    onMounted(() => {
      createChart()
    })

    watch(() => props.data, updateChart, { deep: true })

    return {
      chartCanvas
    }
  }
}
</script>

<style scoped>
.chart-container {
  height: 400px;
  width: 100%;
  padding: 10px;
}
</style> 